/**
 * SmartLawyer Desktop — page de présentation (IA juridique souveraine)
 * Route : /desktop
 */
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PublicFooter, PublicHeader } from '@/components/jurisprudence';

// Installateurs servis depuis le volume public de l'app (bucket smartlawyer-tenants)
const DOWNLOADS = {
  macArm: '/public/downloads/SmartLawyer-mac-arm64.dmg',
  macIntel: '/public/downloads/SmartLawyer-mac-x64.dmg'
};

const PILLARS = [
  { key: 'local', icon: '🧠' },
  { key: 'dossiers', icon: '🔒' },
  { key: 'corpus', icon: '⚖️' },
  { key: 'sources', icon: '📎' }
];

const MACHINES = [
  { key: 'light', accent: 'border-gray-200 dark:border-gray-700' },
  {
    key: 'pro',
    accent:
      'border-violet-400 dark:border-violet-500 ring-2 ring-violet-200 dark:ring-violet-900/50'
  },
  { key: 'cabinet', accent: 'border-gray-200 dark:border-gray-700' }
];

export default function PublicDesktop() {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('desktop.meta.title')}</title>
        <meta name="description" content={t('desktop.meta.description')} />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        <PublicHeader />
        <main className="flex-1 w-full">
          {/* Hero */}
          <section className="bg-gradient-to-br from-slate-950 via-indigo-950 to-violet-950 text-white">
            <div className="max-w-5xl mx-auto px-4 py-20 text-center">
              <span className="inline-block text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full bg-white/10 text-violet-200">
                {t('desktop.badge')}
              </span>
              <h1 className="mt-6 text-4xl md:text-5xl font-bold leading-tight">
                {t('desktop.hero.title')}
              </h1>
              <p className="mt-5 text-lg text-indigo-100 max-w-2xl mx-auto">
                {t('desktop.hero.subtitle')}
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <a
                  href={DOWNLOADS.macArm}
                  download
                  className="px-6 py-3 rounded-xl bg-violet-500 hover:bg-violet-400 font-semibold"
                >
                   {t('desktop.download.mac_arm')}
                </a>
                <a
                  href={DOWNLOADS.macIntel}
                  download
                  className="px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 font-semibold"
                >
                  {t('desktop.download.mac_intel')}
                </a>
                <Link
                  to="/contact"
                  className="px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 font-semibold"
                >
                  {t('desktop.hero.cta')}
                </Link>
                <a
                  href="#machines"
                  className="px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 font-semibold"
                >
                  {t('desktop.hero.secondary')}
                </a>
              </div>
              <p className="mt-6 text-xs text-indigo-200 max-w-xl mx-auto">
                {t('desktop.download.first_open')}
              </p>
              <p className="mt-1 text-xs text-indigo-300/80">
                {t('desktop.download.windows_soon')}
              </p>
            </div>
          </section>

          {/* Piliers */}
          <section className="max-w-5xl mx-auto px-4 py-16 grid sm:grid-cols-2 gap-4">
            {PILLARS.map((p) => (
              <div
                key={p.key}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6"
              >
                <div className="text-2xl">{p.icon}</div>
                <h2 className="mt-3 font-semibold text-gray-900 dark:text-gray-100">
                  {t(`desktop.pillars.${p.key}.title`)}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {t(`desktop.pillars.${p.key}.text`)}
                </p>
              </div>
            ))}
          </section>

          {/* Ce qui sort du poste */}
          <section className="max-w-3xl mx-auto px-4">
            <div className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/30 p-6">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">
                {t('desktop.flow.title')}
              </h2>
              <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                ✅ {t('desktop.flow.out')}
              </p>
              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                🔒 {t('desktop.flow.never')}
              </p>
            </div>
          </section>

          {/* Machines */}
          <section id="machines" className="max-w-5xl mx-auto px-4 py-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center">
              {t('desktop.machines.title')}
            </h2>
            <p className="mt-2 text-center text-gray-500 dark:text-gray-400">
              {t('desktop.machines.subtitle')}
            </p>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {MACHINES.map((m) => (
                <div
                  key={m.key}
                  className={`bg-white dark:bg-gray-900 border rounded-2xl p-6 ${m.accent}`}
                >
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    {t(`desktop.machines.${m.key}.name`)}
                  </h3>
                  <p className="mt-1 text-sm font-mono text-violet-600 dark:text-violet-400">
                    {t(`desktop.machines.${m.key}.spec`)}
                  </p>
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`desktop.machines.${m.key}.text`)}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl mx-auto px-4 pb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              {t('desktop.faq.title')}
            </h2>
            {[1, 2, 3].map((i) => (
              <details
                key={i}
                className="group border-b border-gray-200 dark:border-gray-800 py-4"
              >
                <summary className="cursor-pointer font-medium text-gray-900 dark:text-gray-100">
                  {t(`desktop.faq.q${i}`)}
                </summary>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t(`desktop.faq.a${i}`)}
                </p>
              </details>
            ))}
          </section>

          {/* CTA */}
          <section className="max-w-3xl mx-auto px-4 pb-20 text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('desktop.cta.title')}
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {t('desktop.cta.text')}
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-block px-6 py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-semibold"
            >
              {t('desktop.cta.button')}
            </Link>
          </section>
        </main>
        <PublicFooter />
      </div>
    </>
  );
}
